import React, { useCallback, useEffect, useState } from 'react';
import { elusApi, ELU_TITLES, TITLE_LABEL } from '../../services/elusApi';
import { getToken } from '../auth';
import '../styles/Conseil.css';
import './admin-form.css';

const EMPTY_FORM = {
  firstName: '', lastName: '',
  title: 'conseiller', delegation: '', rank: 0,
  bio: '', quote: '', quoteSource: '',
  email: '', phone: '',
  electedAt: '', mandateEnd: '',
  imageSeed: '', imageUrl: '',
  published: true, order: 0,
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function toInputDate(iso) {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

const Conseil = () => {
  const token = getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterTitle, setFilterTitle] = useState('all');
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await elusApi.adminList(token, {
        title: filterTitle !== 'all' ? filterTitle : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterTitle, query]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setSaveError(null); setOpen(true); };

  const openEdit = async (id) => {
    setEditingId(id);
    setSaveError(null);
    try {
      const { elu } = await elusApi.adminGet(token, id);
      setForm({
        firstName: elu.firstName,
        lastName: elu.lastName,
        title: elu.title,
        delegation: elu.delegation || '',
        rank: elu.rank || 0,
        bio: elu.bio || '',
        quote: elu.quote || '',
        quoteSource: elu.quoteSource || '',
        email: elu.email || '',
        phone: elu.phone || '',
        electedAt: toInputDate(elu.electedAt),
        mandateEnd: toInputDate(elu.mandateEnd),
        imageSeed: elu.imageSeed || '',
        imageUrl: elu.imageUrl || '',
        published: elu.published,
        order: elu.order || 0,
      });
      setOpen(true);
    } catch (err) {
      alert("Impossible de charger l'élu : " + err.message);
    }
  };

  const close = useCallback(() => {
    setOpen(false); setEditingId(null); setForm(EMPTY_FORM); setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        title: form.title,
        delegation: form.delegation,
        rank: parseInt(form.rank, 10) || 0,
        bio: form.bio,
        quote: form.quote,
        quoteSource: form.quoteSource,
        email: form.email,
        phone: form.phone,
        electedAt: form.electedAt || null,
        mandateEnd: form.mandateEnd || null,
        imageSeed: form.imageSeed,
        imageUrl: form.imageUrl,
        published: form.published,
        order: parseInt(form.order, 10) || 0,
      };
      if (editingId) await elusApi.adminUpdate(token, editingId, payload);
      else           await elusApi.adminCreate(token, payload);
      close();
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, fullName) => {
    if (!window.confirm(`Supprimer définitivement « ${fullName} » ?`)) return;
    try {
      await elusApi.adminDelete(token, id);
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  const onTogglePublished = async (id, v) => { try { await elusApi.adminUpdate(token, id, { published: !v }); load(); } catch (e) { alert(e.message); } };

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  const imgFor = (e) => e.imageUrl || `https://picsum.photos/seed/${e.imageSeed || `cb-elu-${e.id}`}/600/600`;
  const titlePillClass = (t) => t === 'maire' ? 'sunset' : t === 'adjoint' ? 'gold' : '';

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Conseil municipal</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher un élu…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
          <button className="top-cta" onClick={openCreate}>
            <span className="plus" /> Nouvel élu
          </button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Conseil municipal · {total} élu{total > 1 ? 's' : ''}
            </span>
            <h1>Le conseil,<br /><span style={{ fontStyle: 'italic' }}>en gestion.</span></h1>
            <p className="sub">Maire, adjoints, conseillers — gestion du mandat 2022–2027. Synchronisé avec /mairie.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={filterTitle}
              onChange={(e) => setFilterTitle(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {ELU_TITLES.map((t) => <option key={t.id} value={t.id}>{t.lbl}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span><span>Erreur : {error}</span>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 56 }} />
                <th>Nom</th>
                <th>Titre</th>
                <th>Délégation</th>
                <th>Rang</th>
                <th>Mandat</th>
                <th>Statut</th>
                <th>Contact</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                  Aucun élu. Cliquez « Nouvel élu » pour démarrer.
                </td></tr>
              )}
              {items.map((e) => (
                <tr key={e.id} onClick={() => openEdit(e.id)}>
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <div
                      style={{
                        width: 44, height: 44, borderRadius: '50%',
                        backgroundImage: `url('${imgFor(e)}')`,
                        backgroundSize: 'cover', backgroundPosition: 'center top',
                        boxShadow: 'inset 0 0 0 1px rgba(20,17,15,.08)',
                      }}
                    />
                  </td>
                  <td><b style={{ fontWeight: 500, fontSize: 14 }}>{e.fullName}</b></td>
                  <td>
                    <span className={`cat-pill ${titlePillClass(e.title)}`}>
                      {TITLE_LABEL[e.title] || e.title}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--char-700)' }}>{e.delegation || '—'}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)', textAlign: 'center' }}>{e.rank}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--char-500)', letterSpacing: '.04em' }}>
                    {e.electedAt && e.mandateEnd
                      ? `${new Date(e.electedAt).getFullYear()} → ${new Date(e.mandateEnd).getFullYear()}`
                      : '—'}
                  </td>
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <span
                      className={`status-pill ${e.published ? 'live' : 'muted'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onTogglePublished(e.id, e.published)}
                      title={e.published ? 'Cliquer pour cacher' : 'Cliquer pour publier'}
                    >
                      {e.published ? 'Publié' : 'Caché'}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>
                    {e.email || '—'}
                  </td>
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <div className="row-actions" style={{ opacity: 1 }}>
                      <button title="Éditer" onClick={() => openEdit(e.id)}>✎</button>
                      <button title="Supprimer" className="danger" onClick={() => onDelete(e.id, e.fullName)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-foot">
            <span>{total} élu{total > 1 ? 's' : ''}</span>
            <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ============ MODALE ============ */}
      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? "Éditer l'élu" : 'Nouvel élu'}</span>
                <h2 className="admin-modal-title">
                  {editingId ? `${form.firstName} ${form.lastName}`.trim() || '—' : 'Ajouter un élu'}
                </h2>
              </div>
              <button onClick={close} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            {/* Aperçu photo */}
            {(form.imageSeed || form.imageUrl) && (
              <div style={{
                width: 120, height: 120, borderRadius: '50%', margin: '0 auto 16px',
                backgroundImage: `url('${form.imageUrl || `https://picsum.photos/seed/${form.imageSeed}/600/600`}')`,
                backgroundSize: 'cover', backgroundPosition: 'center top',
                boxShadow: 'inset 0 0 0 2px rgba(20,17,15,.08)',
              }} />
            )}

            <form onSubmit={onSubmit} className="admin-form">
              <div className="admin-form-row">
                <Field label="Prénom">
                  <input
                    type="text"
                    required
                    value={form.firstName}
                    onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  />
                </Field>
                <Field label="Nom">
                  <input
                    type="text"
                    required
                    value={form.lastName}
                    onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                  />
                </Field>
              </div>

              <div className="admin-form-row">
                <Field label="Titre">
                  <select
                    required
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  >
                    {ELU_TITLES.filter((t) => t.id !== 'all').map((t) =>
                      <option key={t.id} value={t.id}>{t.lbl}</option>
                    )}
                  </select>
                </Field>
                <Field label="Rang (0 = Maire · 1ᵉʳ adj · 2ᵉ adj…)">
                  <input
                    type="number"
                    min={0}
                    value={form.rank}
                    onChange={(e) => setForm((f) => ({ ...f, rank: e.target.value }))}
                  />
                </Field>
              </div>

              <Field label="Délégation / portefeuille">
                <input
                  type="text"
                  value={form.delegation}
                  onChange={(e) => setForm((f) => ({ ...f, delegation: e.target.value }))}
                  placeholder="Ex : Éducation, Voirie & habitat, Maire de Cambérène"
                />
              </Field>

              <Field label="Biographie">
                <textarea
                  rows={3}
                  value={form.bio}
                  onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                  placeholder="Parcours, formations, engagements"
                />
              </Field>

              {form.title === 'maire' && (
                <>
                  <Field label="Citation (apparaît en grand sur la page Mairie)">
                    <textarea
                      rows={3}
                      value={form.quote}
                      onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
                      placeholder="Une phrase qui résume la vision du mandat"
                    />
                  </Field>
                  <Field label="Source de la citation (optionnel)">
                    <input
                      type="text"
                      value={form.quoteSource}
                      onChange={(e) => setForm((f) => ({ ...f, quoteSource: e.target.value }))}
                      placeholder="Discours d'investiture · Mars 2022"
                    />
                  </Field>
                </>
              )}

              <div className="admin-form-row">
                <Field label="Élu·e le">
                  <input
                    type="date"
                    value={form.electedAt}
                    onChange={(e) => setForm((f) => ({ ...f, electedAt: e.target.value }))}
                  />
                </Field>
                <Field label="Fin de mandat">
                  <input
                    type="date"
                    value={form.mandateEnd}
                    onChange={(e) => setForm((f) => ({ ...f, mandateEnd: e.target.value }))}
                  />
                </Field>
              </div>

              <div className="admin-form-row">
                <Field label="Email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="contact@camberene.sn"
                  />
                </Field>
                <Field label="Téléphone">
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+221 …"
                  />
                </Field>
              </div>

              <Field label="Image · seed Picsum ou URL (portrait carré recommandé)">
                <input
                  type="text"
                  value={form.imageSeed || form.imageUrl}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^https?:\/\//.test(v)) setForm((f) => ({ ...f, imageUrl: v, imageSeed: '' }));
                    else setForm((f) => ({ ...f, imageSeed: v, imageUrl: '' }));
                  }}
                  placeholder="cb-mai-portrait-maire  ·  ou  https://…"
                />
              </Field>

              <div className="admin-form-row">
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                  />
                  <span>{form.published ? 'Visible sur /mairie' : 'Caché (brouillon)'}</span>
                </label>
                <Field label="Ordre d'affichage (plus petit = en haut)">
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                  />
                </Field>
              </div>

              {saveError && <div className="admin-form-error">{saveError}</div>}

              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Ajouter')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

function Field({ label, children }) {
  return (
    <label className="admin-field">
      <span className="admin-field-label">{label}</span>
      {children}
    </label>
  );
}

export default Conseil;
